import React, { useRef, useState, useMemo } from 'react';
import { Box, Stack, Center, Portal, Flex, Wrap } from '@chakra-ui/react';
import { useDebounce } from '@hooks/useDebounce';
import { EmptyState, Spinner } from '@ui';
import {
  useATColumns,
  useATFilterAdvanced,
  useATSelection,
  useATisActive,
  ATColumnType,
  ATColumnConfigType,
  ATColumnConfigOptionsType,
  useATFilter,
} from '../hooks';
import { ATProps } from './ATTypes';
import { ATActionBar } from './ATActionBar';
import { TableRows } from '../sub/TableRows';
import { TableHeader } from '../sub/TableHeader';
import ATPagination from './ATPagination';
import { processFilters } from '../filter/FilterLogic';

const EMPTY_ARRAY: any[] = [];

export function ActionTableMain<
  T extends Record<string, any>,
  TExtra extends Record<string, any> = {},
  TContext = any,
>(props: ATProps<T, TExtra, TContext>) {
  const {
    columns: columnsProp,
    columnToUseAsRowKey,
    items,
    onActionBarConfirm,
    isCheckbox = false,
    isSelectionDisabled = false,
    isRowSelectionDisabled,
    isMultipleSelection = true,
    defaultSelections = EMPTY_ARRAY,
    isFilterable = false,
    isSortable = false,
    sortItems,
    isPaginatedAsync = false,
    isPaginated = false,
    LegendComponents,
    totalItemsCount,
    isShowActionBar = false,
    height,
    width = undefined,
    rowHeight = 50,
    columnWidth = EMPTY_ARRAY,
    onRowClick,
    onRowDoubleClick,
    renderCell,
    renderRow,
    renderContext,
    disabled = false,
    isLoading = false,
    ActionBarButtonComponent,
    outerContainerProps,
    ...rest
  } = props;

  const rowKey = columnToUseAsRowKey ?? (columnsProp[0] as keyof (T & TExtra));
  // Selectable
  const { selection } = useATSelection();
  const { isActive, setIsActive } = useATisActive();
  const scrollRef = useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (defaultSelections.length > 0) {
      selection.set(defaultSelections, rowKey);
    }
  }, [defaultSelections, selection, rowKey]);

  // Columns
  const { columns, setColumns, columnsConfig, setColumnsConfig } =
    useATColumns();

  React.useEffect(() => {
    if (!columnsProp || columnsProp.length <= 0) return;
    const tempVisibleColumns: ATColumnType<any>[] = [];
    const tempColumnsConfig: ATColumnConfigType<any> = {};
    columnsProp.forEach((colObj) => {
      const [colKey, colConfig] = Object.entries(colObj)[0] as [
        ATColumnType<T>,
        ATColumnConfigOptionsType,
      ];
      tempVisibleColumns.push(colKey);
      tempColumnsConfig[colKey] = {
        ...colConfig,
        label: colConfig.label ?? colKey,
        width: colConfig.width ?? '150px',
      };
    });
    setColumns(tempVisibleColumns);
    setColumnsConfig(tempColumnsConfig);
  }, [columnsProp]);

  // Filterable
  const { filterValue, filterCols } = useATFilter();
  const { filtersAdvanced } = useATFilterAdvanced();

  const debouncedFilterValue = useDebounce(filterValue, 300);
  const debouncedFiltersAdvanced = useDebounce(filtersAdvanced, 300);

  const filteredItems = useMemo<T[]>(() => {
    if (!isFilterable) {
      return items;
    }

    return processFilters(
      items,
      debouncedFilterValue,
      filterCols,
      debouncedFiltersAdvanced,
    );
  }, [
    items,
    debouncedFiltersAdvanced,
    debouncedFilterValue,
    isFilterable,
    filterCols,
  ]);

  // Sortable
  const [sortKey, setSortKey] = useState<keyof T | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const sortedItems = useMemo<T[]>(() => {
    if (!isSortable || !sortKey) return filteredItems;
    if (sortItems) return sortItems(filteredItems, sortKey, sortOrder);
    return [...filteredItems].sort((a, b) => {
      const va = a[sortKey] ?? '';
      const vb = b[sortKey] ?? '';

      const vaNum =
        typeof va === 'string' && !isNaN(Number(va)) ? Number(va) : va;
      const vbNum =
        typeof vb === 'string' && !isNaN(Number(vb)) ? Number(vb) : vb;

      if (typeof vaNum === 'number' && typeof vbNum === 'number') {
        if (vaNum < vbNum) return sortOrder === 'asc' ? -1 : 1;
        if (vaNum > vbNum) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      }

      // Fallback to string comparison
      const vaStr = va != null ? String(va) : '';
      const vbStr = vb != null ? String(vb) : '';
      if (vaStr < vbStr) return sortOrder === 'asc' ? -1 : 1;
      if (vaStr > vbStr) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredItems, sortKey, sortOrder, isSortable, sortItems]);

  const toggleSort = (col: keyof T) => {
    if (sortKey === col) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(col);
      setSortOrder('asc');
    }
  };

  return (
    <Box
      position="relative"
      maxWidth={width}
      width="100%"
      height="100%"
      onMouseEnter={() => setIsActive(true)}
      onMouseLeave={() => setIsActive(false)}
      pb={`${
        // (isShowActionBar ? 45 : 0) +
        (isPaginated ? 40 : 0) + (isPaginatedAsync ? 40 : 0)
      }px`}
      mb={1}
    >
      <Center
        rounded="md"
        p={2}
        {...(isShowActionBar ? { bg: 'bg.subtle/50' } : {})}
        {...outerContainerProps}
      >
        <Box
          ref={scrollRef}
          _disabled={{
            opacity: 0.5,
            cursor: 'not-allowed',
          }}
          borderWidth="1px"
          rounded="md"
          overflow="auto"
          scrollBehavior="smooth"
          maxHeight={height}
          maxWidth={width}
          // minHeight={height}
          // pb={2}
          // bg="red"
          css={{
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(191, 145, 6, 0.3) rgba(255, 218, 8, 0.3)',
            '&::-webkit-scrollbar': {
              width: '1px',
              height: '1px',
            },
          }}
        >
          {/* ────────────────  Header ──────────────── */}
          <TableHeader<T, TExtra>
            items={sortedItems}
            toggleSort={toggleSort}
            sortKey={sortKey}
            sortOrder={sortOrder}
            isSortable={isSortable}
            isCheckbox={isCheckbox}
            rowKey={rowKey}
            isMultipleSelection={isMultipleSelection}
            isSelectionDisabled={isSelectionDisabled}
            columnWidth={columnWidth}
          />
          {/* ────────────────  Body ──────────────── */}
          <TableRows<T, TExtra, TContext>
            sortedItems={sortedItems}
            columnToUseAsRowKey={rowKey}
            isPaginated={isPaginated}
            isPaginatedAsync={isPaginatedAsync}
            isCheckbox={isCheckbox}
            isSelectionDisabled={isSelectionDisabled}
            isRowSelectionDisabled={isRowSelectionDisabled}
            isMultipleSelection={isMultipleSelection}
            columnWidth={columnWidth}
            rowHeight={rowHeight}
            renderCell={renderCell}
            renderRow={renderRow}
            renderContext={renderContext}
            onRowClick={onRowClick}
            onRowDoubleClick={onRowDoubleClick}
            height={height}
            scrollRef={scrollRef}
            rowKey={rowKey}
          />
        </Box>
      </Center>
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
      >
        {/* ────────────────  Pagination ──────────────── */}
        {(isPaginated || isPaginatedAsync) && (
          <ATPagination
            totalItemsCount={
              isPaginatedAsync
                ? (totalItemsCount ?? sortedItems.length)
                : sortedItems.length
            }
          />
        )}
        {LegendComponents && (
          <Wrap
            my={2}
            mr={2}
            gap={2}
            ml="auto"
            textStyle="body.sm"
            color="fg.subtle"
            fontSize="2xs"
          >
            {LegendComponents.map((component, index) => (
              <React.Fragment key={`legend-component-${index}`}>
                {component}
              </React.Fragment>
            ))}
          </Wrap>
        )}
      </Box>
      {/* ────────────────  Action Bar ──────────────── */}
      {isShowActionBar && (
        <ATActionBar
          onActionBarConfirm={onActionBarConfirm}
          ActionBarButtonComponent={ActionBarButtonComponent}
        />
      )}
      {isLoading && <Spinner isLoading={isLoading} text="Loading..." />}
      {!disabled && isLoading === false && items?.length <= 0 && (
        <EmptyState
          isEmpty={true}
          title="No data found!"
          description={'No data is available to display.'}
        />
      )}
      {disabled && (
        <EmptyState
          isEmpty={true}
          title="No selection!"
          description="Please make a selection and try again."
        />
      )}
    </Box>
  );
}
